import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Send, RefreshCw, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * FirstLoadMatchCampaignCard — admin trigger for the "$10 match waiting"
 * push blast to every player who hasn't yet claimed the new-player match.
 *
 * The eligibility count auto-refreshes on mount so the operator sees the
 * live audience size before firing. Silent no-ops for players without a
 * push subscription (they'll get the promo when they open the app anyway
 * via the MatchReadyChip on lobby).
 */
export const FirstLoadMatchCampaignCard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/promo/first-load-match/eligible-count`,
                { withCredentials: true }
            );
            setStats(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load eligibility count');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const sendPush = async () => {
        if (!stats || stats.push_reachable === 0) {
            toast.info('No players with push subscribed — nothing to send.');
            return;
        }
        const confirmMsg = `Send "🎁 $10 Match Waiting" push to ${stats.push_reachable} players?`;
        if (!window.confirm(confirmMsg)) return;
        setSending(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/promo/first-load-match/send-campaign-push`,
                {},
                { withCredentials: true }
            );
            toast.success(`Push delivered to ${data.delivered}/${data.reachable} players`);
            // Reload count — nothing changes technically, but the operator
            // gets confirmation the audience is still the same.
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Send failed');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="card-dark p-4 space-y-3" data-testid="first-load-match-campaign-card">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <div
                        className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{
                            background: 'linear-gradient(135deg, rgba(57,255,20,0.2), rgba(57,255,20,0.05))',
                            border: '1px solid rgba(57,255,20,0.4)',
                        }}
                    >
                        <Gift className="w-4 h-4 text-[#39FF14]" strokeWidth={2.4} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[13px] font-black text-white leading-tight">
                            $10 Match — Campaign Push
                        </div>
                        <div className="text-[11px] text-[#A0AAB2] mt-0.5">
                            Blast every unclaimed player with a "$10 match waiting" push
                        </div>
                    </div>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="shrink-0 p-1.5 rounded text-[#A0AAB2] hover:text-white transition-colors"
                    data-testid="match-campaign-refresh"
                    aria-label="Refresh count"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
                <div
                    className="rounded-lg py-2"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                    <div className="text-[10px] uppercase tracking-widest text-[#A0AAB2]">Unclaimed</div>
                    <div className="text-lg font-black text-white tabular-nums" data-testid="match-campaign-total-eligible">
                        {stats?.total_eligible ?? '—'}
                    </div>
                </div>
                <div
                    className="rounded-lg py-2"
                    style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.35)' }}
                >
                    <div className="text-[10px] uppercase tracking-widest text-[#39FF14]">Reachable</div>
                    <div className="text-lg font-black text-[#39FF14] tabular-nums" data-testid="match-campaign-reachable">
                        {stats?.push_reachable ?? '—'}
                    </div>
                </div>
                <div
                    className="rounded-lg py-2"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                    <div className="text-[10px] uppercase tracking-widest text-[#A0AAB2]">No Push</div>
                    <div className="text-lg font-black text-white tabular-nums" data-testid="match-campaign-no-push">
                        {stats?.no_push_subscription ?? '—'}
                    </div>
                </div>
            </div>

            <div className="rounded-lg p-2 flex items-center gap-2 text-[11px]"
                 style={{
                     background: 'rgba(255,215,0,0.05)',
                     border: '1px dashed rgba(255,215,0,0.3)',
                 }}>
                <Users className="w-3.5 h-3.5 text-[#FFD700] shrink-0" />
                <span className="text-[#E0E0E0]">
                    Push body: <span className="text-[#FFD700] font-bold">&quot;New player promo: load $10 → we match $10. Tap to load now!&quot;</span>
                </span>
            </div>

            <Button
                onClick={sendPush}
                disabled={sending || !stats || stats.push_reachable === 0}
                className="w-full font-black"
                style={{
                    background: 'linear-gradient(180deg, #39FF14 0%, #2ecc0f 100%)',
                    color: '#062d00',
                }}
                data-testid="match-campaign-send-btn"
            >
                <Send className="w-4 h-4 mr-2" />
                {sending
                    ? 'Sending…'
                    : stats && stats.push_reachable > 0
                    ? `Send Push to ${stats.push_reachable} Players`
                    : 'No reachable players yet'}
            </Button>
        </div>
    );
};

export default FirstLoadMatchCampaignCard;
